import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import useGridOptionsManageOrganizationBase from '../../../hooks/grid/gridOptions/useGridOptionsManageOrganizationBase';
import commonColDef from '../../../data/grid/commonColDef';
import useColdefManageOrganizationBase from '../../../hooks/grid/coldef/useColdefManageOrganizationBase';
import ActionsManageOrganizationBase from '../../../components/cellRenderers/ActionsManageOrganizationBase';
import UserNameFromEmailCellRenderer from '../../../components/cellRenderers/UserNameFromEmailCellRenderer';
import { useTranslation } from 'react-i18next';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { setDialogMode, openDialog, selectOrganizationBase } from '../../../redux/slices/organizationBase';
import type { ColDef, ColumnApi, GridApi, GridReadyEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import type { AgGridReact as AgGridReactType } from 'ag-grid-react/lib/agGridReact';
import type { OrganizationBaseType } from '../../../types/organizationBase';

const GridOrganizationBase = React.forwardRef<AgGridReactType>((props, ref): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isLoading, organizationBases } = useSelector((state: RootState) => state.organizationBase);
  const { users } = useSelector((state: RootState) => state.user);
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsManageOrganizationBase();
  const customColDef: ColDef = {
    cellClass: 'cell',
    flex: 1,
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const columnDefs: ColDef[] = useColdefManageOrganizationBase(t);
  const frameworkComponents = {
    actionsRenderer: ActionsManageOrganizationBase,
    userNameFromEmailCellRenderer: UserNameFromEmailCellRenderer,
  };

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (organizationBases?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, organizationBases]);

  useEffect(() => {
    if (gridApi && gridColumnApi) {
      const columnKeys: string[] = ['updatedAt', 'updatedBy', 'createdAt', 'createdBy'];
      gridColumnApi.autoSizeColumns(columnKeys);
      gridApi.redrawRows();
    }
  }, [gridColumnApi, organizationBases]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  /**
   * ダブルクリックで編集モードにする
   * @param params GridReadyEvent
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    const data: OrganizationBaseType = event.data;
    // console.log(data);
    dispatch(selectOrganizationBase(data));
    dispatch(setDialogMode('UPDATE'));
    dispatch(openDialog());
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <div className="ag-theme-balham" style={{ height: '70vh' }}>
        <AgGridReact
          ref={ref}
          immutableData={true}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={organizationBases}
          onGridReady={onGridReady}
          onRowDoubleClicked={onRowDoubleClicked}
          frameworkComponents={frameworkComponents}
          // filterValueGetterで使うデータ
          context={{
            // reduxにあるものはコピーを渡す
            users: users?.map((user) => structuredClone(user)),
          }}
        />
      </div>
    </>
  );
});

// forwardRefの場合にはlinterに要求される場合がある
// https://stackoverflow.com/questions/67992894/component-definition-is-missing-display-name-for-forwardref
GridOrganizationBase.displayName = 'GridOrganizationBase';
export default GridOrganizationBase;
